import { FC, HTMLAttributes, useState, useRef, useEffect, useMemo } from "react";
import { easeInOutSine } from "utils/ease";
import { Container, useTick, Stage, Text } from "@pixi/react";
import { TextStyle, Ticker, TextMetrics } from "pixi.js";
import { SpinDirection } from "types";
import { useSoundStore } from "utils/sound-store";
import { Sounds } from "types/sound";
import '@pixi/canvas-display';
import '@pixi/canvas-extract';
import '@pixi/canvas-graphics';
import '@pixi/canvas-mesh';
import '@pixi/canvas-particle-container';
import '@pixi/canvas-prepare';
import '@pixi/canvas-renderer';
import '@pixi/canvas-sprite-tiling';
import '@pixi/canvas-sprite';
import '@pixi/canvas-text';

export type SpinnerProps = HTMLAttributes<HTMLDivElement> & {
  textStyle: TextStyle,
  options?: string[],
  spinTo: string,
  spinDirection: SpinDirection,
  width: number,
  height: number,
  spinSeconds?: number,
  skip: boolean,
  onSpinFinish: () => void,
};

const SpinnerContainer: FC<SpinnerProps> = ({ textStyle, options = [], spinTo, spinDirection, spinSeconds = -1, width, height, skip, onSpinFinish }) => {
  const [y, setY] = useState(0);
  const ticker = Ticker.shared;
  const speedRef = useRef(0);
  const deltaRef = useRef(0);
  const indexRef = useRef(0);
  const secRef = useRef(-1);
  const optionRef = useRef<string[]>([]);
  const anchor = {x: 0.5, y: 0.5};
  const play = useSoundStore(store => store.play);
  const stop = useSoundStore(store => store.stop);
  const getHowl = useSoundStore(store => store.getHowl);

  useEffect(() => {
    if(optionRef.current.join() !== options.join()) {
      optionRef.current = [...options];
      indexRef.current = 0;
      setY(0);
    }
  }, [options]);

  useEffect(() => {
    if(spinSeconds < 0) return;
    const newIndex = optionRef.current.indexOf(spinTo);
    const targetY = spinDirection === SpinDirection.Down ? optionRef.current.length * (newIndex < indexRef.current ? 2 : 1) + newIndex - indexRef.current : newIndex - indexRef.current;
    speedRef.current = targetY * height / (spinSeconds * 30);
    deltaRef.current = 0;
    secRef.current = !optionRef.current.length ? -1 : spinSeconds;
  }, [height, spinDirection, spinSeconds, spinTo, stop]);

  useTick(() => {
    if(secRef.current < 0) return;

    const h = optionRef.current.length * height + height;
    const ratio = (deltaRef.current + ticker.deltaMS) / (secRef.current * 500);
    deltaRef.current += ticker.deltaMS;

    const pace = easeInOutSine(ratio) * speedRef.current;
    const sound = Sounds.Spin;
    const howl = getHowl(sound);
    if(pace > 1 && !howl.playing()){
      howl.seek(Math.max(0, howl.duration() - secRef.current * optionRef.current.length));
      play(sound);
    }

    const resetY = height - h;
    let newY = y;
    if(spinDirection === SpinDirection.Down){
      if(newY > 0) {
        newY += resetY;
      }
      newY += pace;
    }else{
      if(newY < resetY) {
        newY -= resetY;
      }
      newY -= pace;
    }

    if(deltaRef.current >= spinSeconds * 1000 || !pace || skip){
      indexRef.current = optionRef.current.indexOf(spinTo);
      newY = spinDirection === SpinDirection.Down ? (indexRef.current + 1) * height - h : indexRef.current * -height;
      onSpinFinish();
      secRef.current = -1;
      if(getHowl(Sounds.Spin).playing()){
        stop(Sounds.Spin);
        play(Sounds.SpinStop);
      }
    }
    
    setY(newY);
  });

  const posY = useMemo(() => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    let y = height / 2;
    if(!!ctx && Number(textStyle.fontWeight) < 600){
      ctx.font = textStyle.toFontString();
      const metrics = TextMetrics.measureText('H', textStyle);
      const browserMetrics = ctx.measureText('H');
      y = (height - browserMetrics.fontBoundingBoxAscent + metrics.fontProperties.fontSize) / 2;
    }
    return y;
  }, [textStyle, height]);

  return (
    <Container position={{ x: 0, y: y }}>
      {spinDirection === SpinDirection.Down && 
        <>
          <Text text={optionRef.current[0] || spinTo} anchor={anchor} x={width / 2} y={posY} style={textStyle} />
          {optionRef.current && [...optionRef.current].reverse().map((value: string, index: number) => <Text key={index} text={value} anchor={anchor} x={width / 2} y={posY + height * (index + 1)} style={textStyle} />)}
        </>
      }
      {spinDirection === SpinDirection.Up && 
        <>
          {optionRef.current && optionRef.current.map((value: string, index: number) => <Text key={index} text={value} anchor={anchor} x={width / 2} y={posY + height * index} style={textStyle} />)}
          <Text text={optionRef.current[0] || spinTo} anchor={anchor} x={width / 2} y={height * optionRef.current.length + posY} style={textStyle} />
        </>
      }
    </Container>
  );
};

export const Spinner: FC<SpinnerProps> = (props) => {
  return (
    <Stage width={props.width} height={props.height} options={{ backgroundAlpha: 0, forceCanvas: true }}>
      <SpinnerContainer {...props} />
    </Stage>
  );
};
